import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/errors/use-toast';
import { AppItem, fetchAppItems } from './database-apps';

export interface UserPinnedApps {
  app_1: string | null;
  app_2: string | null;
  app_3: string | null;
  app_4: string | null;
}

export const useUserPinnedApps = () => {
  return useQuery({
    queryKey: ['user-pinned-apps'], // This key implies "for the current user"
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return null;
      }

      const { data, error } = await supabase
        .from('users')
        .select('app_1, app_2, app_3, app_4')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching pinned apps:', error);
        throw error;
      }
      return data as UserPinnedApps;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    refetchOnWindowFocus: false,
  });
};

export const useUpdatePinnedApp = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ position, appId }: { position: 1 | 2 | 3 | 4; appId: string | null }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const columnName = `app_${position}`;
      const { error } = await supabase
        .from('users')
        .update({ [columnName]: appId })
        .eq('id', user.id);
      
      if (error) throw error;
      return { position, appId, userId: user.id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-pinned-apps'] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update pinned app. Please try again.",
        variant: "destructive",
      });
      console.error('Update pinned app error:', error);
    },
  });
};

export const usePinnedAppsWithDetails = () => {
  const { data: pinnedApps, isLoading: pinnedLoading } = useUserPinnedApps();
  const { data: appItems = [], isLoading: appsLoading } = useQuery({
    queryKey: ['app-items'],
    queryFn: fetchAppItems,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const getPinnedAppDetails = (position: 1 | 2 | 3 | 4): AppItem | null => {
    if (!pinnedApps || !appItems.length) return null;
    
    const appId = pinnedApps[`app_${position}`];
    if (!appId) return null;
    
    return appItems.find(app => app.id === appId) || null;
  };

  const isAppPinned = (appId: string): number | null => {
    if (!pinnedApps) return null;
    
    if (pinnedApps.app_1 === appId) return 1;
    if (pinnedApps.app_2 === appId) return 2;
    if (pinnedApps.app_3 === appId) return 3;
    if (pinnedApps.app_4 === appId) return 4;
    
    return null;
  };

  return {
    pinnedApps,
    appItems,
    getPinnedAppDetails,
    isAppPinned,
    isLoading: pinnedLoading || appsLoading,
  };
};
